import { useCallback } from 'react';

import type { ExternalCharacterListItem } from '@cartwhl/common/api/getExternalCharacters';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import FileIcon from '@cartwhl/ui/icons/FileIcon';
import Button from '@cartwhl/ui/input/Button';
import FilesList, { type FilesListFile } from '@cartwhl/ui/input/FilesList';

import {
  CARTOGRAPHER_ACCEPT_EXTENSIONS,
  CARTOGRAPHER_ALLOWED_MIME_TYPES,
} from '../../constants';
import CartographerCharacterLibrary from '../CharacterLibrary';

/** Character library height in the welcome screen. */
const DESKTOP_LIBRARY_PANEL_HEIGHT_PX = 352;
/** Upload panel height in desktop (kept slightly shorter than library). */
const DESKTOP_UPLOAD_PANEL_HEIGHT_PX = 330;
/** Upload section can be shorter when stacked on small screens. */
const STACKED_UPLOAD_PANEL_HEIGHT_PX = 210;
const LIBRARY_PANEL_COLUMNS = 6;

function CartographerModelUploadRow({
  file,
  onRemove,
}: {
  file: FilesListFile;
  onRemove: () => void;
}) {
  return (
    <div className="group flex h-10 items-center gap-4 rounded-lg px-3">
      <Text variant="studio/body/medium/sm" className="w-8">
        01.
      </Text>
      <Text variant="studio/body/medium/sm" className="flex-1 truncate">
        {file.file.name}
      </Text>
      <Button
        onClick={onRemove}
        variant="tertiary"
        size="sm"
        className="text-secondary opacity-0 transition-opacity group-hover:opacity-100"
        aria-label={`Remove ${file.file.name}`}
        icon={<CrossIcon />}
      />
    </div>
  );
}

const CARTOGRAPHER_DOCS_URL =
  'https://docs.getcartwheel.com/docs/custom-characters/cartographer';
const CHARACTER_UPLOAD_DOCS_URL =
  'https://docs.getcartwheel.com/docs/custom-characters/cartographer';

type CartographerWelcomeScreenProps = {
  apiKey: string | null;
  characters: ExternalCharacterListItem[];
  charactersLoading: boolean;
  charactersError: string | null;
  loadingCharacterId: string | null;
  onSelectCharacter: (character: ExternalCharacterListItem) => void;
  onLoadExistingCharacter: () => void;
  onModelFileSelected: (file: File) => void;
};

export default function CartographerWelcomeScreen({
  apiKey,
  characters,
  charactersLoading,
  charactersError,
  loadingCharacterId,
  onSelectCharacter,
  onLoadExistingCharacter,
  onModelFileSelected,
}: CartographerWelcomeScreenProps) {
  const isDesktopLayout = useMediaQuery('(min-width: 1024px)');
  const libraryPanelHeight = isDesktopLayout
    ? DESKTOP_LIBRARY_PANEL_HEIGHT_PX
    : DESKTOP_UPLOAD_PANEL_HEIGHT_PX;
  const uploadPanelHeight = isDesktopLayout
    ? DESKTOP_UPLOAD_PANEL_HEIGHT_PX
    : STACKED_UPLOAD_PANEL_HEIGHT_PX;

  const handleFilesChange = useCallback(
    (files: FilesListFile[]) => {
      const next = files[0]?.file;
      if (next) {
        onModelFileSelected(next);
      }
    },
    [onModelFileSelected],
  );

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <div className="container mx-auto py-12">
        <div className="mb-5 flex items-start justify-between gap-6">
          <div className="flex flex-1 flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Text
                variant="studio/display/xl"
                className="font-display text-[40px] leading-[48px]"
                as="h1"
              >
                Cartographer
              </Text>
              <Text variant="studio/body/medium/24" className="text-tertiary">
                Character rig mapping
              </Text>
            </div>
            <Text
              variant="studio/body/md"
              className="text-secondary max-w-[602px]"
            >
              Cartographer helps map Cartwheel motion onto your rigged models
              using IK chain mapping, joint limits & offsets, and other
              properties.
            </Text>
          </div>
          <a
            href={CARTOGRAPHER_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              variant="secondary"
              alignIcon="end"
              icon={
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-2"
                >
                  <path
                    d="M5.83333 14.1667L14.1667 5.83333M14.1667 5.83333H5.83333M14.1667 5.83333V14.1667"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            >
              Learn more about Cartographer
            </Button>
          </a>
        </div>

        <div className="bg-secondary rounded-2xl p-4">
          <div className="border-gray-30 bg-primary grid grid-cols-1 overflow-hidden rounded-xl border lg:grid-cols-[minmax(0,0.76fr)_auto_minmax(0,1.24fr)]">
            <div className="border-gray-30 bg-primary order-1 flex flex-col gap-6 border-b p-6 lg:order-3 lg:border-b-0 lg:pl-6">
              <Text variant="studio/body/medium/24" className="text-primary" as="h2">
                Load existing character
              </Text>
              <CartographerCharacterLibrary
                mode="compact"
                apiKey={apiKey}
                characters={characters}
                charactersLoading={charactersLoading}
                charactersError={charactersError}
                loadingCharacterId={loadingCharacterId}
                onSelectCharacter={onSelectCharacter}
                onViewAll={onLoadExistingCharacter}
                toolbar={{
                  showSearch: true,
                  showExpandButton: true,
                  onExpandClick: onLoadExistingCharacter,
                  expandTooltipText: 'Open full character library',
                  expandAriaLabel: 'Open full character library',
                }}
                compactHeightPx={libraryPanelHeight}
                compactColumns={LIBRARY_PANEL_COLUMNS}
                className="max-lg:border-r lg:border-r-0"
              />
            </div>

            <div
              className="border-gray-30 bg-secondary text-tertiary order-2 flex flex-row items-center justify-center gap-3 border-b px-4 py-5 lg:w-16 lg:flex-col lg:gap-2 lg:border-r lg:border-b-0 lg:px-2 lg:py-10"
              role="separator"
              aria-label="Or choose the other option"
            >
              <span className="bg-gray-30 dark:bg-gray-60 h-px flex-1 lg:h-12 lg:w-px lg:flex-none" />
              <Text
                as="span"
                variant="studio/body/medium/sm"
                className="text-tertiary font-medium tracking-wide"
              >
                OR
              </Text>
              <span className="bg-gray-30 dark:bg-gray-60 h-px flex-1 lg:h-12 lg:w-px lg:flex-none" />
            </div>

            <div className="border-gray-30 bg-primary order-3 flex flex-col gap-6 p-5 lg:order-1 lg:border-r lg:pr-4">
              <Text
                variant="studio/body/medium/24"
                className="text-primary"
                as="h2"
              >
                Upload a rigged model
              </Text>
              <div className="flex flex-col gap-2">
                <FilesList
                  allowedMimeTypes={CARTOGRAPHER_ALLOWED_MIME_TYPES}
                  fallbackExtensions={CARTOGRAPHER_ACCEPT_EXTENSIONS}
                  maxFiles={1}
                  height={uploadPanelHeight}
                  onFilesChange={handleFilesChange}
                  renderItem={(file, removeFile) => (
                    <CartographerModelUploadRow
                      file={file}
                      onRemove={removeFile}
                    />
                  )}
                  emptyStateContent={
                    <div className="flex items-center gap-1">
                      <FileIcon className="text-blue" />
                      <Text variant="studio/body/medium/sm">
                        <span className="text-blue">Select a model</span>{' '}
                        <span className="text-gray-60">or drop it here</span>
                      </Text>
                    </div>
                  }
                  dragOverlayContent={
                    <Text variant="studio/title/xl" className="text-white">
                      Drop model to upload
                    </Text>
                  }
                />
                <Text
                  as="div"
                  variant="studio/body/medium/sm"
                  className="flex items-center justify-between px-2"
                >
                  <a
                    href={CHARACTER_UPLOAD_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue hover:underline"
                  >
                    Upload guidelines
                  </a>
                  <span className="text-tertiary">.glb, .gltf, .fbx</span>
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
